import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../components/Heading';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Text, Strong } from '../../components/Text';
import { Input } from '../../components/Input';
import { Select } from '../../components/Select';
import { Checkbox } from '../../components/Checkbox';
import Loading from '../../components/Loading';
import showErrorToast from '../../components/Toasts/ShowErrorToast';
import showSuccessToast from '../../components/Toasts/ShowSuccessToast';
import { useAiReportHelper } from '../../hooks/useAiReportHelper';
import {
    AiReportChatMessage,
    AiReportColumnMapping,
    AiReportConversion,
    AiReportCustomFill,
    AiReportSavedTemplate,
    AiReportSummary,
    AiReportTemplateColumn,
} from '../../models/AiReportHelper';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../components/Dialog';
import DatasetApplyDialog from './DatasetApplyDialog';
import ColumnFillWizard from './ColumnFillWizard';
import { describeFill } from './fillUtils';
import {
    ArrowDownIcon,
    ArrowUpIcon,
    BookmarkIcon,
    ChartBarIcon,
    CircleStackIcon,
    Cog8ToothIcon,
    DocumentArrowDownIcon,
    DocumentIcon,
    EyeIcon,
    PaperAirplaneIcon,
    PaperClipIcon,
    PencilIcon,
    PlusIcon,
    QuestionMarkCircleIcon,
    SparklesIcon,
    TrashIcon,
    XMarkIcon,
} from '@heroicons/react/20/solid';

const TRIAL_END = 'Aug 31, 2026';

const COMMON_FIELDS = [
    'Sales Rep Code', 'Sales Rep Name', 'Customer Code', 'Customer Name',
    'Material Code', 'Material Name', 'Pack Size', 'Transaction Type',
    'Invoice Date', 'Invoice No', 'Batch No', 'Qty Sold', 'Sales Value',
    'Customer Group', 'District', 'City', 'State', 'Reason of Return',
];

interface BubbleBase { id: string; time: number }
type Bubble = BubbleBase & (
    | { role: 'user'; kind: 'text'; text: string }
    | { role: 'user'; kind: 'attachment'; fileName: string; meta?: string; which: 'source' | 'template' }
    | { role: 'assistant'; kind: 'text'; text: string }
    | { role: 'assistant'; kind: 'template-choice' }
    | { role: 'assistant'; kind: 'analytics-choice'; hasCurrentFile: boolean }
    | { role: 'assistant'; kind: 'template-checklist' }
    | { role: 'assistant'; kind: 'mapping' }
    | { role: 'assistant'; kind: 'generated' }
);

type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

function newId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatTime(ms: number): string {
    return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function AssistantAvatar() {
    return (
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-violet-600 text-white">
            <SparklesIcon className="size-4" />
        </div>
    );
}

function ThinkingIndicator() {
    return (
        <div className="flex items-center gap-1 pt-2.5">
            <span className="size-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.3s]" />
            <span className="size-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.15s]" />
            <span className="size-1.5 animate-bounce rounded-full bg-zinc-400" />
        </div>
    );
}

function greetingBubbles(): Bubble[] {
    return [
        {
            id: newId(),
            time: Date.now(),
            role: 'assistant',
            kind: 'text',
            text: "Hi! Attach the report you'd like to convert using the paperclip button below, and I'll take it from there.",
        },
    ];
}

// Stage-specific timestamp columns (template_set_time/mapping_set_time/generated_time) are only
// populated from the Phase 1 migration onward - older conversions have them NULL even though the
// stage clearly happened (the file/mapping/summary is right there). Falling back straight to
// Date.now() for those made an old, untouched conversation look like it was just modified today.
// Fall back to created_time instead (guaranteed present, and always <= the real stage time) so a
// resumed old conversation's bubbles read as historical, never as "now".
function rebuildBubbles(c: AiReportConversion, messages: AiReportChatMessage[]): Bubble[] {
    const fallbackTime = Date.parse(c.created_time) || Date.now();
    const bubbles: Bubble[] = [
        { id: newId(), time: Date.now(), role: 'assistant', kind: 'text', text: "Welcome back — here's where you left off." },
    ];
    if (c.source_file_name) {
        bubbles.push({
            id: newId(), time: fallbackTime, role: 'user', kind: 'attachment', fileName: c.source_file_name,
            meta: c.source_row_count != null ? `${c.source_row_count} rows` : undefined, which: 'source',
        });
    }
    if (c.template_file_name) {
        bubbles.push({
            id: newId(), time: Date.parse(c.template_set_time || '') || fallbackTime, role: 'user', kind: 'attachment', fileName: c.template_file_name,
            meta: c.template_headers ? `${c.template_headers.length} columns` : undefined, which: 'template',
        });
    }
    if (c.column_mapping) bubbles.push({ id: newId(), time: Date.parse(c.mapping_set_time || '') || fallbackTime, role: 'assistant', kind: 'mapping' });
    if (c.generated_summary) bubbles.push({ id: newId(), time: Date.parse(c.generated_time || '') || fallbackTime, role: 'assistant', kind: 'generated' });
    messages.forEach(m => bubbles.push({
        id: `msg-${m.id}`, time: Date.parse(m.created_time) || fallbackTime, role: m.role, kind: 'text', text: m.message,
    }));
    return bubbles;
}

function formatHistoryTimestamp(iso: string): string {
    return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const AIReportHelperPage: React.FC = () => {
    const navigate = useNavigate();
    const {
        loading,
        history,
        uploadSource,
        uploadTemplate,
        buildTemplate,
        suggestMapping,
        resolveFillInstruction,
        suggestFillOptions,
        generate,
        previewDatasetApply,
        addTemplateColumns,
        resolveEditInstruction,
        applyEdit,
        downloadGeneratedReport,
        chat,
        classifyIntent,
        fetchHistory,
        fetchById,
        deleteById,
        updateMeta,
        previewFile,
        listSavedTemplates,
        saveAsTemplate,
        uploadSavedTemplateFile,
        addSavedTemplate,
        renameSavedTemplate,
        loadSavedTemplate,
        deleteSavedTemplate,
    } = useAiReportHelper();

    const [bubbles, setBubbles] = useState<Bubble[]>(greetingBubbles());
    const [conversion, setConversion] = useState<AiReportConversion | null>(null);
    const [sourceHeaders, setSourceHeaders] = useState<string[]>([]);
    const [templateHeaders, setTemplateHeaders] = useState<AiReportTemplateColumn[]>([]);
    const [mapping, setMapping] = useState<AiReportColumnMapping>({});
    const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
    const [configuringColumnId, setConfiguringColumnId] = useState<string | null>(null);
    const [summary, setSummary] = useState<AiReportSummary | null>(null);
    const [pendingAttachTarget, setPendingAttachTarget] = useState<'source' | 'template' | 'analyze' | null>('source');
    const [checklistSelected, setChecklistSelected] = useState<Set<string>>(new Set());
    const [checklistCustom, setChecklistCustom] = useState<string[]>([]);
    const [customFieldDraft, setCustomFieldDraft] = useState('');
    const [chatInput, setChatInput] = useState('');
    const [sending, setSending] = useState(false);
    const [mappingBusy, setMappingBusy] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [buildingTemplate, setBuildingTemplate] = useState(false);
    const [mappingCollapsed, setMappingCollapsed] = useState(false);
    const [guideOpen, setGuideOpen] = useState(false);
    const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
    const [columnNameDraft, setColumnNameDraft] = useState('');
    const [outputFilenameDraft, setOutputFilenameDraft] = useState('');
    const [editingHistoryId, setEditingHistoryId] = useState<number | null>(null);
    const [historyNameDraft, setHistoryNameDraft] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewTitle, setPreviewTitle] = useState('');
    const [previewData, setPreviewData] = useState<{
        headers: string[]; rows: Record<string, any>[]; totalRows: number; truncated: boolean;
    } | null>(null);
    const [savedTemplatesOpen, setSavedTemplatesOpen] = useState(false);
    const [savedTemplatesLoading, setSavedTemplatesLoading] = useState(false);
    const [savedTemplates, setSavedTemplates] = useState<AiReportSavedTemplate[]>([]);
    const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
    const [saveTemplateNameDraft, setSaveTemplateNameDraft] = useState('');
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [addTemplateOpen, setAddTemplateOpen] = useState(false);
    const [addTemplateStep, setAddTemplateStep] = useState<'upload' | 'select'>('upload');
    const [addTemplateName, setAddTemplateName] = useState('');
    const [addTemplateFile, setAddTemplateFile] = useState<File | null>(null);
    const [addTemplateUploading, setAddTemplateUploading] = useState(false);
    const [addTemplateFileMeta, setAddTemplateFileMeta] = useState<{ templateFileName: string; templateFileUrl: string } | null>(null);
    const [addTemplateHeaders, setAddTemplateHeaders] = useState<AiReportTemplateColumn[]>([]);
    const [addTemplateSelectedIds, setAddTemplateSelectedIds] = useState<Set<string>>(new Set());
    const [addTemplateSaving, setAddTemplateSaving] = useState(false);
    const [renameTemplateTarget, setRenameTemplateTarget] = useState<AiReportSavedTemplate | null>(null);
    const [renameTemplateDraft, setRenameTemplateDraft] = useState('');
    const [renamingTemplate, setRenamingTemplate] = useState(false);
    const [viewTemplateTarget, setViewTemplateTarget] = useState<AiReportSavedTemplate | null>(null);
    const addTemplateFileInputRef = useRef<HTMLInputElement>(null);
    const [editReportOpen, setEditReportOpen] = useState(false);
    const [editTargetColumn, setEditTargetColumn] = useState('');
    const [editInstruction, setEditInstruction] = useState('');
    const [editResolvedFill, setEditResolvedFill] = useState<AiReportCustomFill | null>(null);
    const [editResolving, setEditResolving] = useState(false);
    const [editApplying, setEditApplying] = useState(false);

    const [datasetApplyOpen, setDatasetApplyOpen] = useState(false);
    // Set when opened from a single column's "Fill from a Data Set" wizard choice; null = the
    // toolbar's bulk "Add columns from Data Set" entry point.
    const [datasetApplyLockedColumnId, setDatasetApplyLockedColumnId] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        document.title = 'AI Report Helper';
        fetchHistory().catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [bubbles]);

    const push = (b: DistributiveOmit<Bubble, 'id' | 'time'>) =>
        setBubbles(prev => [...prev, { ...b, id: newId(), time: Date.now() } as Bubble]);

    const resetWorkflow = () => {
        setConversion(null);
        setSourceHeaders([]);
        setTemplateHeaders([]);
        setMapping({});
        setSelectedColumns(new Set());
        setSummary(null);
        setChecklistSelected(new Set());
        setChecklistCustom([]);
        setCustomFieldDraft('');
        setChatInput('');
        setPendingAttachTarget('source');
        setMappingCollapsed(false);
        setConfiguringColumnId(null);
        setEditingColumnId(null);
        setColumnNameDraft('');
        setOutputFilenameDraft('');
        setBubbles(greetingBubbles());
    };

    const runSuggestMapping = async (conversionId: number, headers: string[]) => {
        setMappingBusy(true);
        setMappingCollapsed(false);
        try {
            const res = await suggestMapping(conversionId);
            setConversion(res.conversion);
            const nextSelected = new Set<string>();
            Object.entries(res.mapping).forEach(([col, rule]) => {
                if (rule.sourceColumn) nextSelected.add(col);
            });
            setMapping(res.mapping);
            setSelectedColumns(nextSelected);
            push({ role: 'assistant', kind: 'text', text: 'Here\'s my suggested mapping — review it, tick the columns you want, then generate.' });
        } catch (err: any) {
            const emptyMapping: AiReportColumnMapping = {};
            headers.forEach(h => { emptyMapping[h] = {}; });
            setMapping(emptyMapping);
            setSelectedColumns(new Set());
            push({ role: 'assistant', kind: 'text', text: "I couldn't work out a mapping automatically, but you can still set it manually below." });
        } finally {
            setMappingBusy(false);
            push({ role: 'assistant', kind: 'mapping' });
        }
    };

    const handleSourceFile = async (file: File) => {
        push({ role: 'user', kind: 'attachment', fileName: file.name, which: 'source' });
        setPendingAttachTarget(null);
        try {
            const res = await uploadSource(file);
            resetWorkflowKeepingBubbles();
            setConversion(res.conversion);
            setSourceHeaders(res.headers);
            push({
                role: 'assistant', kind: 'text',
                text: `Got it — ${res.rowCount} rows, ${res.headers.length} columns. Now, how would you like to set the target template?`,
            });
            push({ role: 'assistant', kind: 'template-choice' });
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to upload that report');
            push({ role: 'assistant', kind: 'text', text: "Sorry, I couldn't read that file — make sure it's a .xlsx and try again." });
            setPendingAttachTarget('source');
        }
    };

    // Uploading a new source file mid-conversation should clear downstream state
    // (template/mapping/summary) without wiping the transcript itself.
    const resetWorkflowKeepingBubbles = () => {
        setTemplateHeaders([]);
        setMapping({});
        setSelectedColumns(new Set());
        setSummary(null);
        setMappingCollapsed(false);
        setConfiguringColumnId(null);
        setEditingColumnId(null);
        setOutputFilenameDraft('');
    };

    const handleTemplateFile = async (file: File) => {
        if (!conversion) return;
        push({ role: 'user', kind: 'attachment', fileName: file.name, which: 'template' });
        try {
            const res = await uploadTemplate(conversion.id, file);
            setConversion(res.conversion);
            setTemplateHeaders(res.conversion.template_headers || []);
            push({ role: 'assistant', kind: 'text', text: `Loaded "${file.name}" — ${res.headers.length} columns. Suggesting a mapping…` });
            await runSuggestMapping(conversion.id, res.headers);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to upload that template');
            push({ role: 'assistant', kind: 'text', text: "Couldn't read that template — try another .xlsx file." });
            setPendingAttachTarget('template');
        }
    };

    const handleAnalyzeFile = async (file: File) => {
        push({ role: 'user', kind: 'attachment', fileName: file.name, which: 'source' });
        setPendingAttachTarget(null);
        try {
            const res = await uploadSource(file);
            navigate(`/gp/ai-report-helper/analytics/${res.conversion.id}`);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to upload that file');
            setPendingAttachTarget('analyze');
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (pendingAttachTarget === 'source') handleSourceFile(file);
        else if (pendingAttachTarget === 'template') handleTemplateFile(file);
        else if (pendingAttachTarget === 'analyze') handleAnalyzeFile(file);
    };

    const handleChooseAnalyzeUpload = () => {
        setPendingAttachTarget('analyze');
        fileInputRef.current?.click();
    };

    const handleChooseAnalyzeCurrent = () => {
        if (!conversion) return;
        navigate(`/gp/ai-report-helper/analytics/${conversion.id}`);
    };

    const handleChooseUploadTemplate = () => {
        setPendingAttachTarget('template');
        fileInputRef.current?.click();
    };

    const handleChooseBuildTemplate = () => {
        push({ role: 'assistant', kind: 'template-checklist' });
    };

    const handleOpenSavedTemplates = async () => {
        setSavedTemplatesOpen(true);
        setSavedTemplatesLoading(true);
        try {
            const res = await listSavedTemplates();
            setSavedTemplates(res.templates);
        } catch (err: any) {
            showErrorToast('Failed to load saved templates');
        } finally {
            setSavedTemplatesLoading(false);
        }
    };

    const handleLoadSavedTemplate = async (template: AiReportSavedTemplate) => {
        if (!conversion) return;
        setSavedTemplatesOpen(false);
        try {
            const res = await loadSavedTemplate(conversion.id, template.id);
            setConversion(res.conversion);
            setTemplateHeaders(res.conversion.template_headers || []);
            setMapping(res.conversion.column_mapping || {});
            setSelectedColumns(new Set(res.selectedColumns || []));
            const droppedNote = res.droppedColumnCount > 0
                ? ` ${res.droppedColumnCount} column${res.droppedColumnCount === 1 ? '' : 's'} couldn't be matched to your current source file and need${res.droppedColumnCount === 1 ? 's' : ''} remapping.`
                : '';
            push({ role: 'assistant', kind: 'text', text: `Loaded template "${template.name}" with its saved mapping.${droppedNote}` });
            push({ role: 'assistant', kind: 'mapping' });
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to load that template');
        }
    };

    const handleDeleteSavedTemplate = async (templateId: number) => {
        try {
            await deleteSavedTemplate(templateId);
            setSavedTemplates(prev => prev.filter(t => t.id !== templateId));
        } catch (err: any) {
            showErrorToast('Failed to delete that saved template');
        }
    };

    const handleOpenAddTemplateDialog = () => {
        setAddTemplateStep('upload');
        setAddTemplateName('');
        setAddTemplateFile(null);
        setAddTemplateFileMeta(null);
        setAddTemplateHeaders([]);
        setAddTemplateSelectedIds(new Set());
        setAddTemplateOpen(true);
    };

    const handleAddTemplateFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) setAddTemplateFile(file);
    };

    const handleAddTemplateUpload = async () => {
        if (!addTemplateFile) return;
        setAddTemplateUploading(true);
        try {
            const res = await uploadSavedTemplateFile(addTemplateFile);
            setAddTemplateFileMeta({ templateFileName: res.templateFileName, templateFileUrl: res.templateFileUrl });
            setAddTemplateHeaders(res.templateHeaders);
            setAddTemplateSelectedIds(new Set(res.templateHeaders.map(h => h.id)));
            setAddTemplateStep('select');
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || "Couldn't read that file — try another .xlsx file.");
        } finally {
            setAddTemplateUploading(false);
        }
    };

    const handleToggleAddTemplateColumn = (id: string) => {
        setAddTemplateSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleSaveAddTemplate = async () => {
        const name = addTemplateName.trim();
        if (!name || !addTemplateFileMeta || addTemplateSelectedIds.size === 0) return;
        setAddTemplateSaving(true);
        try {
            const selectedIds = addTemplateHeaders.filter(h => addTemplateSelectedIds.has(h.id)).map(h => h.id);
            const res = await addSavedTemplate(name, addTemplateFileMeta.templateFileName, addTemplateFileMeta.templateFileUrl, addTemplateHeaders, selectedIds);
            setSavedTemplates(prev => [res.template, ...prev]);
            showSuccessToast(`Added template "${name}"`);
            setAddTemplateOpen(false);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to add this template');
        } finally {
            setAddTemplateSaving(false);
        }
    };

    const handleOpenRenameTemplate = (template: AiReportSavedTemplate) => {
        setRenameTemplateTarget(template);
        setRenameTemplateDraft(template.name);
    };

    const handleSaveRenameTemplate = async () => {
        if (!renameTemplateTarget) return;
        const name = renameTemplateDraft.trim();
        if (!name) return;
        setRenamingTemplate(true);
        try {
            const res = await renameSavedTemplate(renameTemplateTarget.id, name);
            setSavedTemplates(prev => prev.map(t => (t.id === res.template.id ? res.template : t)));
            setRenameTemplateTarget(null);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to rename that template');
        } finally {
            setRenamingTemplate(false);
        }
    };

    const handleOpenSaveTemplateDialog = () => {
        setSaveTemplateNameDraft('');
        setSaveTemplateDialogOpen(true);
    };

    const handleSaveTemplate = async () => {
        if (!conversion) return;
        const name = saveTemplateNameDraft.trim();
        if (!name) {
            showErrorToast('Give this template a name');
            return;
        }
        setSavingTemplate(true);
        try {
            await saveAsTemplate(conversion.id, name, templateHeaders, mapping, getOrderedSelectedIds());
            showSuccessToast(`Saved template "${name}"`);
            setSaveTemplateDialogOpen(false);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to save this template');
        } finally {
            setSavingTemplate(false);
        }
    };

    const openDatasetApplyBulk = () => {
        setDatasetApplyLockedColumnId(null);
        setDatasetApplyOpen(true);
    };

    const openDatasetApplyForColumn = (columnId: string) => {
        setDatasetApplyLockedColumnId(columnId);
        setDatasetApplyOpen(true);
    };

    const handleDatasetApplied = (result: {
        conversion?: AiReportConversion;
        templateHeaders?: AiReportTemplateColumn[];
        mappingPatch: AiReportColumnMapping;
        selectedColumnIds: string[];
        toastMessage: string;
    }) => {
        if (result.conversion) setConversion(result.conversion);
        if (result.templateHeaders) setTemplateHeaders(result.templateHeaders);
        setMapping(prev => ({ ...prev, ...result.mappingPatch }));
        setSelectedColumns(prev => {
            const next = new Set(prev);
            result.selectedColumnIds.forEach(id => next.add(id));
            return next;
        });
        showSuccessToast(result.toastMessage);
    };

    const toggleChecklistField = (field: string) => {
        setChecklistSelected(prev => {
            const next = new Set(prev);
            if (next.has(field)) next.delete(field);
            else next.add(field);
            return next;
        });
    };

    const handleAddCustomField = () => {
        const field = customFieldDraft.trim();
        if (!field || checklistCustom.includes(field) || COMMON_FIELDS.includes(field)) return;
        setChecklistCustom(prev => [...prev, field]);
        setChecklistSelected(prev => new Set(prev).add(field));
        setCustomFieldDraft('');
    };

    const handleGenerateTemplate = async () => {
        if (!conversion) return;
        const columns = [...COMMON_FIELDS, ...checklistCustom].filter(f => checklistSelected.has(f));
        if (columns.length === 0) {
            showErrorToast('Pick at least one column first');
            return;
        }
        push({ role: 'user', kind: 'text', text: `Build a template with: ${columns.join(', ')}` });
        setBuildingTemplate(true);
        try {
            const res = await buildTemplate(conversion.id, columns);
            setConversion(res.conversion);
            setTemplateHeaders(res.conversion.template_headers || []);
            push({ role: 'assistant', kind: 'text', text: `Built a template with ${res.headers.length} columns. Suggesting a mapping…` });
            await runSuggestMapping(conversion.id, res.headers);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to build a template');
        } finally {
            setBuildingTemplate(false);
        }
    };

    const toggleColumn = (col: string) => {
        setSelectedColumns(prev => {
            const next = new Set(prev);
            if (next.has(col)) next.delete(col);
            else next.add(col);
            return next;
        });
    };

    const handleSaveColumnRule = (col: string, rule: { sourceColumn: string | null; customFill: AiReportCustomFill | null }) => {
        setMapping(prev => ({ ...prev, [col]: rule }));
        if (rule.sourceColumn || rule.customFill) setSelectedColumns(prev => new Set(prev).add(col));
    };

    const handleClearColumnRule = (col: string) => {
        setMapping(prev => ({ ...prev, [col]: { sourceColumn: null, customFill: null } }));
        setSelectedColumns(prev => {
            const next = new Set(prev);
            next.delete(col);
            return next;
        });
    };

    const selectAllColumns = () => setSelectedColumns(new Set(templateHeaders.map(h => h.id)));
    const selectNoColumns = () => setSelectedColumns(new Set());

    const moveColumn = (id: string, direction: -1 | 1) => {
        setTemplateHeaders(prev => {
            const idx = prev.findIndex(h => h.id === id);
            const swapIdx = idx + direction;
            if (idx < 0 || swapIdx < 0 || swapIdx >= prev.length) return prev;
            const next = [...prev];
            [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
            return next;
        });
    };

    const startEditColumnName = (col: AiReportTemplateColumn) => {
        setEditingColumnId(col.id);
        setColumnNameDraft(col.displayName || col.originalName);
    };

    const commitColumnName = (col: AiReportTemplateColumn) => {
        const trimmed = columnNameDraft.trim();
        setTemplateHeaders(prev => prev.map(h =>
            h.id === col.id ? { ...h, displayName: trimmed && trimmed !== h.originalName ? trimmed : null } : h
        ));
        setEditingColumnId(null);
    };

    // templateHeaders' array order IS the current column order (reordering splices this same
    // array - see moveColumn), so filtering it by selection preserves whatever order the user
    // set, not just Set insertion order. Shared by Generate and Save-as-template.
    const getOrderedSelectedIds = () => templateHeaders.filter(h => selectedColumns.has(h.id)).map(h => h.id);

    const handleGenerate = async () => {
        if (!conversion) return;
        if (selectedColumns.size === 0) {
            showErrorToast('Select at least one column to generate');
            return;
        }
        setGenerating(true);
        try {
            const orderedSelectedIds = getOrderedSelectedIds();
            // Renames only ever live in local state (see the plan's locked decision: rename
            // affects just this output, never the saved template) - send them explicitly so
            // the backend can apply them for this generate call.
            const columnRenames: Record<string, string> = {};
            templateHeaders.forEach(h => { if (h.displayName) columnRenames[h.id] = h.displayName; });
            const res = await generate(conversion.id, orderedSelectedIds, mapping, columnRenames);
            setConversion(res.conversion);
            setMapping(res.conversion.column_mapping || {});
            setSummary(res.summary);
            setMappingCollapsed(true);
            setConfiguringColumnId(null);
            setOutputFilenameDraft(res.conversion.generated_file_name || '');
            push({ role: 'assistant', kind: 'generated' });
            if (res.droppedColumnCount > 0) {
                showErrorToast(
                    `Generated report with ${res.rowCount} rows, but ${res.droppedColumnCount} column${res.droppedColumnCount === 1 ? '' : 's'} couldn't be matched to your source file and were left blank - remap ${res.droppedColumnCount === 1 ? 'it' : 'them'} and generate again.`
                );
            } else {
                showSuccessToast(`Generated report with ${res.rowCount} rows`);
            }
            fetchHistory().catch(() => { });
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to generate the report');
        } finally {
            setGenerating(false);
        }
    };

    const handleSaveFilename = async () => {
        if (!conversion) return;
        const trimmed = outputFilenameDraft.trim();
        if (!trimmed || trimmed === conversion.generated_file_name) return;
        try {
            const res = await updateMeta(conversion.id, { generated_file_name: trimmed });
            setConversion(res.conversion);
            fetchHistory().catch(() => { });
        } catch (err: any) {
            showErrorToast('Failed to save the new filename');
        }
    };

    const handleDownload = async () => {
        if (!conversion) return;
        try {
            const blob = await downloadGeneratedReport(conversion.id);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = outputFilenameDraft.trim() || conversion.generated_file_name || 'report.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            showErrorToast('Failed to download the generated report');
        }
    };

    const handleOpenEditReport = () => {
        setEditTargetColumn(summary ? Object.keys(summary.columns)[0] || '' : '');
        setEditInstruction('');
        setEditResolvedFill(null);
        setEditReportOpen(true);
    };

    const handlePreviewEdit = async () => {
        if (!conversion || !editTargetColumn || !editInstruction.trim()) return;
        setEditResolving(true);
        setEditResolvedFill(null);
        try {
            const res = await resolveEditInstruction(conversion.id, editTargetColumn, editInstruction.trim());
            if (res.customFill.mode === 'unsupported') {
                showErrorToast(res.customFill.reason || "Couldn't work out how to apply that edit");
                return;
            }
            setEditResolvedFill(res.customFill);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to resolve that edit');
        } finally {
            setEditResolving(false);
        }
    };

    const handleApplyEdit = async () => {
        if (!conversion || !editTargetColumn || !editResolvedFill) return;
        setEditApplying(true);
        try {
            const res = await applyEdit(conversion.id, editTargetColumn, editResolvedFill);
            setConversion(res.conversion);
            setSummary(res.summary);
            setEditReportOpen(false);
            push({ role: 'assistant', kind: 'text', text: `Applied edit to "${editTargetColumn}".` });
            showSuccessToast('Edit applied');
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to apply that edit');
        } finally {
            setEditApplying(false);
        }
    };

    const handleSendChat = async () => {
        const message = chatInput.trim();
        if (!message) return;
        setChatInput('');
        push({ role: 'user', kind: 'text', text: message });

        setSending(true);
        try {
            const { intent } = await classifyIntent(message);
            if (intent === 'analysis') {
                const hasCurrentFile = !!conversion?.source_file_url;
                push({
                    role: 'assistant', kind: 'text',
                    text: hasCurrentFile
                        ? "Sure — want to analyze this conversation's file, or upload a different one?"
                        : "Sure — upload a file and I'll open it up for analysis.",
                });
                push({ role: 'assistant', kind: 'analytics-choice', hasCurrentFile });
                return;
            }
        } catch (err: any) {
            // Classification failing shouldn't block normal chat - fall through.
        } finally {
            setSending(false);
        }

        if (!conversion || !summary) {
            push({ role: 'assistant', kind: 'text', text: "Let's finish generating a report first — I'll need that before I can answer questions about it." });
            return;
        }

        setSending(true);
        try {
            const res = await chat(conversion.id, message);
            push({ role: 'assistant', kind: 'text', text: res.reply });
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to get a response');
        } finally {
            setSending(false);
        }
    };

    const handleLoadHistory = async (id: number) => {
        try {
            const res = await fetchById(id);
            const c = res.conversion;
            setConversion(c);
            setSourceHeaders(c.source_headers || []);
            setTemplateHeaders(c.template_headers || []);
            setMapping(c.column_mapping || {});
            setSelectedColumns(new Set(c.selected_columns || []));
            setSummary(c.generated_summary || null);
            setPendingAttachTarget(c.source_file_name ? (c.template_file_name ? null : 'template') : 'source');
            setMappingCollapsed(!!c.generated_summary);
            setConfiguringColumnId(null);
            setOutputFilenameDraft(c.generated_file_name || '');
            setBubbles(rebuildBubbles(c, res.messages || []));
        } catch (err: any) {
            showErrorToast('Failed to load that report');
        }
    };

    const handleDeleteHistory = async (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await deleteById(id);
            if (conversion?.id === id) resetWorkflow();
        } catch (err: any) {
            showErrorToast('Failed to delete that report');
        }
    };

    const startEditHistoryName = (item: { id: number; title: string | null; source_file_name: string | null }, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingHistoryId(item.id);
        setHistoryNameDraft(item.title || item.source_file_name || '');
    };

    const commitHistoryName = async (id: number) => {
        const trimmed = historyNameDraft.trim();
        setEditingHistoryId(null);
        if (!trimmed) return;
        try {
            const res = await updateMeta(id, { title: trimmed });
            if (conversion?.id === id) setConversion(res.conversion);
        } catch (err: any) {
            showErrorToast('Failed to rename that report');
        }
    };

    const handlePreview = async (which: 'source' | 'template' | 'generated', title: string) => {
        if (!conversion) return;
        setPreviewTitle(title);
        setPreviewOpen(true);
        setPreviewLoading(true);
        setPreviewData(null);
        try {
            const res = await previewFile(conversion.id, which);
            setPreviewData({ headers: res.headers, rows: res.rows, totalRows: res.totalRows, truncated: res.truncated });
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to load preview');
            setPreviewOpen(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const attachEnabled = pendingAttachTarget !== null;

    return (
        <div className="flex h-[calc(100vh-8rem)] flex-col">
            <style>{`
                @keyframes bubbleIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
                .bubble-enter { animation: bubbleIn 0.2s ease-out; }
                @media (prefers-reduced-motion: reduce) { .bubble-enter { animation: none; } }
            `}</style>
            <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleFileChange} />

            {/* Full-width header, spans above both the sub-sidebar and the conversation */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-4">
                <div className="flex items-center gap-3">
                    <Heading>AI Report Helper</Heading>
                    <Badge color="violet">Beta</Badge>
                    <button
                        onClick={() => setGuideOpen(true)}
                        className="text-zinc-400 hover:text-zinc-950 dark:hover:text-white"
                        aria-label="How this works"
                        title="How this works"
                    >
                        <QuestionMarkCircleIcon className="size-5" />
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <Button outline className="md:hidden" onClick={resetWorkflow} aria-label="New chat">
                        <PlusIcon />
                    </Button>
                    <Badge color="amber" className="whitespace-nowrap">Free trial until {TRIAL_END}</Badge>
                </div>
            </div>

            {/* Action bar: quick links to settings pages that live outside the chat flow */}
            <div className="flex items-center gap-2 py-2">
                <Button outline onClick={() => navigate('/gp/ai-report-helper/datasets')}>
                    <Cog8ToothIcon />
                    Data Sets
                </Button>
                <Button outline onClick={() => navigate('/gp/ai-report-helper/templates')}>
                    <BookmarkIcon />
                    Templates
                </Button>
            </div>

            <div className="flex min-h-0 flex-1 gap-6 pt-4">
                {/* Sub-sidebar: New chat + History, ChatGPT-style */}
                <Card className="hidden w-56 shrink-0 flex-col p-3 md:flex">
                    <Button className="w-full justify-center" onClick={resetWorkflow}>
                        <PlusIcon />
                        New chat
                    </Button>
                    <div className="mt-5 min-h-0 flex-1 overflow-y-auto">
                        <Text className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">History</Text>
                        <div className="space-y-0.5">
                            {history.length === 0 && <Text className="px-2 text-sm">No reports yet</Text>}
                            {history.map(item => (
                                editingHistoryId === item.id ? (
                                    <div key={item.id} className="flex items-center gap-1 rounded-lg px-2 py-1.5">
                                        <Input
                                            autoFocus
                                            value={historyNameDraft}
                                            onChange={(e) => setHistoryNameDraft(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && commitHistoryName(item.id)}
                                            onBlur={() => commitHistoryName(item.id)}
                                            className="!py-1 text-sm"
                                        />
                                    </div>
                                ) : (
                                    <button
                                        key={item.id}
                                        onClick={() => handleLoadHistory(item.id)}
                                        className={`group flex w-full items-center justify-between gap-2 rounded-lg px-2 py-2 text-left text-sm ${conversion?.id === item.id
                                                ? 'bg-zinc-950/5 dark:bg-white/10'
                                                : 'hover:bg-zinc-950/5 dark:hover:bg-white/5'
                                            }`}
                                    >
                                        <span className="truncate text-zinc-700 dark:text-zinc-300">
                                            {item.title || item.source_file_name || formatHistoryTimestamp(item.created_time)}
                                        </span>
                                        <span className="flex shrink-0 items-center gap-1 opacity-0 group-hover:opacity-100">
                                            <PencilIcon
                                                className="size-3.5 text-zinc-400 hover:text-zinc-950 dark:hover:text-white"
                                                onClick={(e) => startEditHistoryName(item, e)}
                                            />
                                            <TrashIcon
                                                className="size-3.5 text-zinc-400 hover:text-red-600"
                                                onClick={(e) => handleDeleteHistory(item.id, e)}
                                            />
                                        </span>
                                    </button>
                                )
                            ))}
                        </div>
                    </div>
                </Card>

                <div className="flex min-w-0 flex-1 flex-col">
                    <Card className="flex min-h-0 flex-1 flex-col p-4">
                    <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
                    {bubbles.map((b, idx) => (
                        <BubbleView
                            key={b.id}
                            bubble={b}
                            showAvatar={b.role === 'assistant' && bubbles[idx - 1]?.role !== 'assistant'}
                            templateHeaders={templateHeaders}
                            mapping={mapping}
                            selectedColumns={selectedColumns}
                            mappingBusy={mappingBusy}
                            generating={generating}
                            summary={summary}
                            conversion={conversion}
                            checklistSelected={checklistSelected}
                            checklistCustom={checklistCustom}
                            customFieldDraft={customFieldDraft}
                            buildingTemplate={buildingTemplate}
                            mappingCollapsed={mappingCollapsed}
                            editingColumnId={editingColumnId}
                            columnNameDraft={columnNameDraft}
                            outputFilenameDraft={outputFilenameDraft}
                            onChooseUploadTemplate={handleChooseUploadTemplate}
                            onChooseBuildTemplate={handleChooseBuildTemplate}
                            onOpenSavedTemplates={handleOpenSavedTemplates}
                            onChooseAnalyzeUpload={handleChooseAnalyzeUpload}
                            onChooseAnalyzeCurrent={handleChooseAnalyzeCurrent}
                            onOpenSaveTemplateDialog={handleOpenSaveTemplateDialog}
                            onOpenDatasetApply={openDatasetApplyBulk}
                            onToggleChecklistField={toggleChecklistField}
                            onCustomFieldDraftChange={setCustomFieldDraft}
                            onAddCustomField={handleAddCustomField}
                            onGenerateTemplate={handleGenerateTemplate}
                            onToggleColumn={toggleColumn}
                            onSelectAllColumns={selectAllColumns}
                            onSelectNoColumns={selectNoColumns}
                            onConfigureColumn={setConfiguringColumnId}
                            onClearColumnRule={handleClearColumnRule}
                            onMoveColumn={moveColumn}
                            onStartEditColumnName={startEditColumnName}
                            onColumnNameDraftChange={setColumnNameDraft}
                            onCommitColumnName={commitColumnName}
                            onGenerate={handleGenerate}
                            onExpandMapping={() => setMappingCollapsed(false)}
                            onFilenameDraftChange={setOutputFilenameDraft}
                            onSaveFilename={handleSaveFilename}
                            onDownload={handleDownload}
                            onPreview={handlePreview}
                            onOpenEditReport={handleOpenEditReport}
                        />
                    ))}
                    {(sending || (loading && !mappingBusy && !generating && !buildingTemplate)) && (
                        <div className="flex items-start gap-3 py-2">
                            <AssistantAvatar />
                            <ThinkingIndicator />
                        </div>
                    )}
                    <div ref={bottomRef} />
                </div>

                <div className="flex items-center gap-2 pt-3">
                    <Button
                        outline
                        disabled={!attachEnabled}
                        onClick={() => fileInputRef.current?.click()}
                        aria-label="Attach file"
                    >
                        <PaperClipIcon />
                    </Button>
                    <Input
                        placeholder={
                            pendingAttachTarget === 'source'
                                ? 'Attach your report to begin…'
                                : pendingAttachTarget === 'template'
                                    ? 'Attach your template file…'
                                    : summary
                                        ? 'Ask a question about this report…'
                                        : 'Type a message…'
                        }
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
                    />
                    <Button onClick={handleSendChat} disabled={!chatInput.trim()}>
                        <PaperAirplaneIcon />
                    </Button>
                    </div>
                    </Card>
                </div>
            </div>

            <Dialog open={guideOpen} onClose={() => setGuideOpen(false)} size="lg">
                <DialogTitle>How AI Report Helper works</DialogTitle>
                <DialogBody>
                    <ol className="list-decimal space-y-3 pl-5 text-sm text-zinc-700 dark:text-zinc-300">
                        <li>
                            <Strong>Attach your report.</Strong> Click the paperclip and upload the .xlsx file you want to convert.
                        </li>
                        <li>
                            <Strong>Set a target template.</Strong> Either upload an existing template file, or let AI build one for you by picking columns from a checklist.
                        </li>
                        <li>
                            <Strong>Review the mapping.</Strong> AI suggests which source column feeds each template column. Tick the columns you want, and click "Configure" on any column to change how it's filled — map it from a different source column, always use the same value, ask AI (e.g. "always Sarawak"), pull it from a Data Set, or use a dropdown/checkbox.
                        </li>
                        <li>
                            <Strong>Generate the report.</Strong> Once you're happy with the mapping, click Generate — you'll get a downloadable .xlsx file.
                        </li>
                        <li>
                            <Strong>Ask follow-up questions.</Strong> After generating, use the chat box to ask questions about the resulting report.
                        </li>
                        <li>
                            <Strong>Revisit past reports.</Strong> Everything is saved to History in the left panel — click any entry to pick up where you left off.
                        </li>
                    </ol>
                </DialogBody>
                <DialogActions>
                    <Button onClick={() => setGuideOpen(false)}>Got it</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} size="4xl">
                <DialogTitle>{previewTitle}</DialogTitle>
                <DialogBody>
                    {previewLoading && <div className="flex justify-center py-10"><Loading /></div>}
                    {!previewLoading && previewData && (
                        <>
                            {previewData.truncated && (
                                <Text className="mb-2 text-xs !text-zinc-500">
                                    Showing first {previewData.rows.length} of {previewData.totalRows} rows
                                </Text>
                            )}
                            <div className="max-h-[60vh] overflow-auto rounded-lg border border-zinc-950/10 dark:border-white/10">
                                <table className="min-w-full divide-y divide-zinc-950/5 text-sm dark:divide-white/5">
                                    <thead className="sticky top-0 bg-zinc-50 dark:bg-zinc-800">
                                        <tr>
                                            {previewData.headers.map(h => (
                                                <th key={h} className="whitespace-nowrap px-3 py-2 text-left font-semibold text-zinc-700 dark:text-zinc-200">
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-950/5 dark:divide-white/5">
                                        {previewData.rows.map((row, i) => (
                                            <tr key={i}>
                                                {previewData!.headers.map(h => (
                                                    <td key={h} className="whitespace-nowrap px-3 py-2 text-zinc-600 dark:text-zinc-300">
                                                        {String(row[h] ?? '')}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                        {previewData.rows.length === 0 && (
                                            <tr>
                                                <td colSpan={previewData.headers.length || 1} className="px-3 py-6 text-center text-zinc-400">
                                                    No rows
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </DialogBody>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={savedTemplatesOpen} onClose={() => setSavedTemplatesOpen(false)} size="lg">
                <DialogTitle>Saved templates</DialogTitle>
                <DialogBody>
                    <div className="mb-3 flex justify-end">
                        <Button outline onClick={handleOpenAddTemplateDialog}>
                            <PlusIcon />
                            Add template
                        </Button>
                    </div>
                    {savedTemplatesLoading && <div className="flex justify-center py-10"><Loading /></div>}
                    {!savedTemplatesLoading && savedTemplates.length === 0 && (
                        <Text className="py-6 text-center !text-zinc-500">No saved templates yet.</Text>
                    )}
                    {!savedTemplatesLoading && savedTemplates.length > 0 && (
                        <ul className="divide-y divide-zinc-950/5 dark:divide-white/5">
                            {savedTemplates.map((t) => (
                                <li key={t.id} className="flex items-center justify-between gap-3 py-3">
                                    <div className="min-w-0">
                                        <div className="truncate text-sm font-medium text-zinc-950 dark:text-white">{t.name}</div>
                                        <div className="text-xs text-zinc-500">
                                            {(t.template_headers || []).length} columns · {t.template_file_name}
                                        </div>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                        <button
                                            className="rounded p-1 text-zinc-400 hover:text-zinc-950 dark:hover:text-white"
                                            onClick={() => setViewTemplateTarget(t)}
                                            aria-label={`View ${t.name}`}
                                            title="View columns"
                                        >
                                            <EyeIcon className="size-4" />
                                        </button>
                                        <button
                                            className="rounded p-1 text-zinc-400 hover:text-zinc-950 dark:hover:text-white"
                                            onClick={() => handleOpenRenameTemplate(t)}
                                            aria-label={`Rename ${t.name}`}
                                            title="Rename"
                                        >
                                            <PencilIcon className="size-4" />
                                        </button>
                                        <Button outline onClick={() => handleLoadSavedTemplate(t)}>Load</Button>
                                        <button
                                            className="rounded p-1 text-zinc-400 hover:text-red-600"
                                            onClick={() => handleDeleteSavedTemplate(t.id)}
                                            aria-label={`Delete ${t.name}`}
                                            title="Delete"
                                        >
                                            <TrashIcon className="size-4" />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </DialogBody>
                <DialogActions>
                    <Button onClick={() => setSavedTemplatesOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={addTemplateOpen} onClose={() => setAddTemplateOpen(false)}>
                <DialogTitle>Add template</DialogTitle>
                <DialogBody>
                    {addTemplateStep === 'upload' && (
                        <div className="space-y-3">
                            <Text>Give it a name and upload the .xlsx file to save.</Text>
                            <Input
                                autoFocus
                                placeholder="Template name, e.g. Global Pharma Duo Daily Report"
                                value={addTemplateName}
                                onChange={(e) => setAddTemplateName(e.target.value)}
                            />
                            <input
                                ref={addTemplateFileInputRef}
                                type="file"
                                accept=".xlsx"
                                className="hidden"
                                onChange={handleAddTemplateFileChange}
                            />
                            <button
                                type="button"
                                className="flex w-full items-center gap-2 rounded-lg border border-dashed border-zinc-950/15 px-3 py-3 text-sm text-zinc-500 hover:border-violet-400 hover:text-violet-600 dark:border-white/15"
                                onClick={() => addTemplateFileInputRef.current?.click()}
                            >
                                <DocumentIcon className="size-5 shrink-0" />
                                {addTemplateFile ? addTemplateFile.name : 'Choose a .xlsx file'}
                            </button>
                        </div>
                    )}
                    {addTemplateStep === 'select' && (
                        <div className="space-y-3">
                            <Text>Choose which columns to keep for this template.</Text>
                            <div className="max-h-72 divide-y divide-zinc-950/5 overflow-y-auto rounded-lg border border-zinc-950/10 dark:divide-white/5 dark:border-white/10">
                                {addTemplateHeaders.map((col) => (
                                    <label key={col.id} className="flex items-center gap-2 px-3 py-2.5 text-sm text-zinc-950 dark:text-white">
                                        <Checkbox
                                            checked={addTemplateSelectedIds.has(col.id)}
                                            onChange={() => handleToggleAddTemplateColumn(col.id)}
                                        />
                                        {col.displayName || col.originalName}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setAddTemplateOpen(false)} disabled={addTemplateUploading || addTemplateSaving}>
                        Cancel
                    </Button>
                    {addTemplateStep === 'upload' && (
                        <Button onClick={handleAddTemplateUpload} disabled={addTemplateUploading || !addTemplateName.trim() || !addTemplateFile}>
                            {addTemplateUploading ? <Loading /> : 'Next'}
                        </Button>
                    )}
                    {addTemplateStep === 'select' && (
                        <Button onClick={handleSaveAddTemplate} disabled={addTemplateSaving || addTemplateSelectedIds.size === 0}>
                            {addTemplateSaving ? <Loading /> : 'Save'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={!!renameTemplateTarget} onClose={() => setRenameTemplateTarget(null)}>
                <DialogTitle>Rename template</DialogTitle>
                <DialogBody>
                    <Input
                        autoFocus
                        value={renameTemplateDraft}
                        onChange={(e) => setRenameTemplateDraft(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveRenameTemplate()}
                    />
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setRenameTemplateTarget(null)} disabled={renamingTemplate}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveRenameTemplate} disabled={renamingTemplate || !renameTemplateDraft.trim()}>
                        {renamingTemplate ? <Loading /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!viewTemplateTarget} onClose={() => setViewTemplateTarget(null)}>
                <DialogTitle>{viewTemplateTarget?.name}</DialogTitle>
                <DialogBody>
                    <Text className="mb-2 !text-zinc-500">{viewTemplateTarget?.template_file_name}</Text>
                    <ul className="max-h-80 divide-y divide-zinc-950/5 overflow-y-auto rounded-lg border border-zinc-950/10 dark:divide-white/5 dark:border-white/10">
                        {(viewTemplateTarget?.template_headers || []).map((col) => (
                            <li key={col.id} className="px-3 py-2 text-sm text-zinc-950 dark:text-white">{col.displayName || col.originalName}</li>
                        ))}
                    </ul>
                </DialogBody>
                <DialogActions>
                    <Button onClick={() => setViewTemplateTarget(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={saveTemplateDialogOpen} onClose={() => setSaveTemplateDialogOpen(false)}>
                <DialogTitle>Save as template</DialogTitle>
                <DialogBody>
                    <Text className="mb-2">
                        Save this template's structure and current mapping so it can be reused for future reports.
                    </Text>
                    <Input
                        autoFocus
                        placeholder="Template name, e.g. Global Pharma Duo Daily Report"
                        value={saveTemplateNameDraft}
                        onChange={(e) => setSaveTemplateNameDraft(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveTemplate()}
                    />
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setSaveTemplateDialogOpen(false)} disabled={savingTemplate}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveTemplate} disabled={savingTemplate || !saveTemplateNameDraft.trim()}>
                        {savingTemplate ? <Loading /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <DatasetApplyDialog
                open={datasetApplyOpen}
                onClose={() => setDatasetApplyOpen(false)}
                conversion={conversion}
                sourceHeaders={sourceHeaders}
                templateHeaders={templateHeaders}
                mapping={mapping}
                lockedTargetColumnId={datasetApplyLockedColumnId}
                previewDatasetApply={previewDatasetApply}
                addTemplateColumns={addTemplateColumns}
                onApplied={handleDatasetApplied}
            />

            <ColumnFillWizard
                open={configuringColumnId !== null}
                onClose={() => setConfiguringColumnId(null)}
                column={templateHeaders.find(h => h.id === configuringColumnId) || null}
                rule={configuringColumnId ? mapping[configuringColumnId] : undefined}
                sourceHeaders={sourceHeaders}
                conversionId={conversion?.id ?? null}
                resolveFillInstruction={resolveFillInstruction}
                suggestFillOptions={suggestFillOptions}
                onSave={handleSaveColumnRule}
                onChooseDataSet={openDatasetApplyForColumn}
            />

            <Dialog open={editReportOpen} onClose={() => setEditReportOpen(false)} size="lg">
                <DialogTitle>Edit report</DialogTitle>
                <DialogBody className="space-y-3">
                    <Text>Describe a change and preview it before it's applied to every row.</Text>
                    <Select
                        value={editTargetColumn}
                        onChange={(e) => { setEditTargetColumn(e.target.value); setEditResolvedFill(null); }}
                    >
                        {Object.keys(summary?.columns || {}).map(col => (
                            <option key={col} value={col}>{col}</option>
                        ))}
                    </Select>
                    <Input
                        placeholder='e.g. "change all Sarawak entries to Sabah"'
                        value={editInstruction}
                        onChange={(e) => { setEditInstruction(e.target.value); setEditResolvedFill(null); }}
                        onKeyDown={(e) => e.key === 'Enter' && handlePreviewEdit()}
                    />
                    {!editResolvedFill && (
                        <Button
                            outline
                            onClick={handlePreviewEdit}
                            disabled={editResolving || !editTargetColumn || !editInstruction.trim()}
                        >
                            {editResolving ? 'Resolving…' : 'Preview change'}
                        </Button>
                    )}
                    {editResolvedFill && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-500/30 dark:bg-amber-500/10">
                            <Strong>This will apply to every row: </Strong>
                            {describeFill({ customFill: editResolvedFill })}
                        </div>
                    )}
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setEditReportOpen(false)} disabled={editApplying}>
                        Cancel
                    </Button>
                    {editResolvedFill && (
                        <Button onClick={handleApplyEdit} disabled={editApplying}>
                            {editApplying ? <Loading /> : 'Apply'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
};

interface BubbleViewProps {
    bubble: Bubble;
    showAvatar: boolean;
    templateHeaders: AiReportTemplateColumn[];
    mapping: AiReportColumnMapping;
    selectedColumns: Set<string>;
    mappingBusy: boolean;
    generating: boolean;
    summary: AiReportSummary | null;
    conversion: AiReportConversion | null;
    checklistSelected: Set<string>;
    checklistCustom: string[];
    customFieldDraft: string;
    buildingTemplate: boolean;
    mappingCollapsed: boolean;
    editingColumnId: string | null;
    columnNameDraft: string;
    outputFilenameDraft: string;
    onChooseUploadTemplate: () => void;
    onChooseBuildTemplate: () => void;
    onOpenSavedTemplates: () => void;
    onChooseAnalyzeUpload: () => void;
    onChooseAnalyzeCurrent: () => void;
    onOpenSaveTemplateDialog: () => void;
    onOpenDatasetApply: () => void;
    onToggleChecklistField: (field: string) => void;
    onCustomFieldDraftChange: (val: string) => void;
    onAddCustomField: () => void;
    onGenerateTemplate: () => void;
    onToggleColumn: (col: string) => void;
    onSelectAllColumns: () => void;
    onSelectNoColumns: () => void;
    onConfigureColumn: (col: string) => void;
    onClearColumnRule: (col: string) => void;
    onMoveColumn: (id: string, direction: -1 | 1) => void;
    onStartEditColumnName: (col: AiReportTemplateColumn) => void;
    onColumnNameDraftChange: (val: string) => void;
    onCommitColumnName: (col: AiReportTemplateColumn) => void;
    onGenerate: () => void;
    onExpandMapping: () => void;
    onFilenameDraftChange: (val: string) => void;
    onSaveFilename: () => void;
    onDownload: () => void;
    onPreview: (which: 'source' | 'template' | 'generated', title: string) => void;
    onOpenEditReport: () => void;
}

const BubbleView: React.FC<BubbleViewProps> = ({
    bubble, showAvatar, templateHeaders, mapping, selectedColumns,
    mappingBusy, generating, summary, conversion, checklistSelected, checklistCustom, customFieldDraft,
    buildingTemplate, mappingCollapsed,
    editingColumnId, columnNameDraft, outputFilenameDraft,
    onChooseUploadTemplate, onChooseBuildTemplate, onOpenSavedTemplates, onOpenSaveTemplateDialog, onOpenDatasetApply,
    onChooseAnalyzeUpload, onChooseAnalyzeCurrent,
    onToggleChecklistField, onCustomFieldDraftChange, onAddCustomField, onGenerateTemplate, onToggleColumn,
    onSelectAllColumns, onSelectNoColumns, onConfigureColumn, onClearColumnRule,
    onMoveColumn, onStartEditColumnName, onColumnNameDraftChange, onCommitColumnName,
    onGenerate, onExpandMapping, onFilenameDraftChange, onSaveFilename, onDownload, onPreview, onOpenEditReport,
}) => {
    const isUser = bubble.role === 'user';
    let content: React.ReactNode = null;

    if (bubble.kind === 'text') {
        content = isUser ? (
            <div className="inline-block max-w-full rounded-2xl bg-zinc-950 px-3.5 py-2 text-sm whitespace-pre-wrap text-white dark:bg-white dark:text-zinc-950">
                {bubble.text}
            </div>
        ) : (
            <Text className="whitespace-pre-wrap !text-zinc-700 dark:!text-zinc-300">{bubble.text}</Text>
        );
    } else if (bubble.kind === 'attachment') {
        content = (
            <div className="inline-flex max-w-full items-center gap-2.5 rounded-xl border border-zinc-950/10 bg-white px-3 py-2 dark:border-white/10 dark:bg-zinc-900">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400">
                    <PaperClipIcon className="size-4" />
                </div>
                <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-zinc-950 dark:text-white">{bubble.fileName}</div>
                    {bubble.meta && <div className="text-xs text-zinc-500 dark:text-zinc-400">{bubble.meta}</div>}
                </div>
                <button
                    className="shrink-0 rounded p-1 text-zinc-400 hover:text-violet-600"
                    onClick={() => onPreview(bubble.which, bubble.fileName)}
                    title="Preview"
                    aria-label="Preview file"
                >
                    <EyeIcon className="size-4" />
                </button>
            </div>
        );
    } else if (bubble.kind === 'template-choice') {
        content = (
            <div className="max-w-md space-y-2.5">
                <Text>How would you like to set the target template?</Text>
                <div className="flex flex-wrap gap-2">
                    <Button outline onClick={onChooseUploadTemplate}>
                        <PaperClipIcon />
                        Upload a template file
                    </Button>
                    <Button outline onClick={onChooseBuildTemplate}>
                        <SparklesIcon />
                        Build one for me
                    </Button>
                    <Button outline onClick={onOpenSavedTemplates}>
                        <BookmarkIcon />
                        Load a saved template
                    </Button>
                </div>
            </div>
        );
    } else if (bubble.kind === 'analytics-choice') {
        content = (
            <div className="max-w-md space-y-2.5">
                <div className="flex flex-wrap gap-2">
                    {bubble.hasCurrentFile && (
                        <Button outline onClick={onChooseAnalyzeCurrent}>
                            <ChartBarIcon />
                            Use this conversation's file
                        </Button>
                    )}
                    <Button outline onClick={onChooseAnalyzeUpload}>
                        <PaperClipIcon />
                        Upload a new file
                    </Button>
                </div>
            </div>
        );
    } else if (bubble.kind === 'template-checklist') {
        const allFields = [...COMMON_FIELDS, ...checklistCustom];
        content = (
            <div className="w-full max-w-xl rounded-xl border border-zinc-950/10 bg-white p-4 dark:border-white/10 dark:bg-zinc-900">
                <Text className="mb-3">Pick the columns you want in your template:</Text>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                    {allFields.map(field => (
                        <label key={field} className="flex items-center gap-2 text-sm">
                            <Checkbox checked={checklistSelected.has(field)} onChange={() => onToggleChecklistField(field)} />
                            {field}
                        </label>
                    ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                    <Input
                        placeholder="Add a custom column…"
                        value={customFieldDraft}
                        onChange={(e) => onCustomFieldDraftChange(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && onAddCustomField()}
                    />
                    <Button outline onClick={onAddCustomField} disabled={!customFieldDraft.trim()}>
                        <PlusIcon />
                    </Button>
                </div>
                <div className="mt-4 flex justify-end">
                    <Button onClick={onGenerateTemplate} disabled={buildingTemplate || checklistSelected.size === 0}>
                        {buildingTemplate ? 'Building…' : `Generate template (${checklistSelected.size} columns)`}
                    </Button>
                </div>
            </div>
        );
    } else if (bubble.kind === 'mapping') {
        content = mappingCollapsed ? (
            <div className="flex w-full max-w-xl flex-wrap items-center justify-between gap-3 rounded-xl border border-zinc-950/10 bg-white p-3 dark:border-white/10 dark:bg-zinc-900">
                <Text>
                    <Strong>{selectedColumns.size}</Strong> of {templateHeaders.length} columns mapped
                </Text>
                <Button outline onClick={onExpandMapping}>
                    <PencilIcon />
                    Edit mapping
                </Button>
            </div>
        ) : (
            <div className="w-full max-w-xl rounded-xl border border-zinc-950/10 bg-white p-4 dark:border-white/10 dark:bg-zinc-900">
                {mappingBusy && <Loading />}
                {!mappingBusy && (
                    <>
                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                            <Text>
                                <Strong>{selectedColumns.size}</Strong> of {templateHeaders.length} selected
                            </Text>
                            <div className="flex flex-wrap gap-3">
                                <button className="text-xs text-zinc-500 hover:text-zinc-950 dark:hover:text-white" onClick={onSelectAllColumns}>
                                    Select all
                                </button>
                                <button className="text-xs text-zinc-500 hover:text-zinc-950 dark:hover:text-white" onClick={onSelectNoColumns}>
                                    Clear
                                </button>
                                <button className="text-xs text-zinc-500 hover:text-zinc-950 dark:hover:text-white" onClick={onOpenSaveTemplateDialog}>
                                    Save as template
                                </button>
                                <button className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800" onClick={onOpenDatasetApply}>
                                    <CircleStackIcon className="size-3.5" />
                                    Add columns from Data Set
                                </button>
                            </div>
                        </div>

                        <div className="max-h-72 divide-y divide-zinc-950/5 overflow-y-auto rounded-lg border border-zinc-950/10 dark:divide-white/5 dark:border-white/10">
                            {templateHeaders.map((col, colIdx) => {
                                const rule = mapping[col.id];
                                const fillDescription = describeFill(rule);
                                const statusText = fillDescription || (rule?.sourceColumn ? `From: ${rule.sourceColumn}` : 'Not configured');
                                const hasRule = !!(rule?.sourceColumn || rule?.customFill);
                                const isEditingName = editingColumnId === col.id;
                                const label = col.displayName || col.originalName;
                                return (
                                    <div key={col.id} className="flex items-center gap-2 px-3 py-2.5">
                                        <Checkbox checked={selectedColumns.has(col.id)} onChange={() => onToggleColumn(col.id)} />
                                        <div className="flex shrink-0 flex-col">
                                            <button
                                                className="text-zinc-400 hover:text-zinc-950 disabled:opacity-30 dark:hover:text-white"
                                                onClick={() => onMoveColumn(col.id, -1)}
                                                disabled={colIdx === 0}
                                                aria-label="Move column up"
                                                title="Move up"
                                            >
                                                <ArrowUpIcon className="size-3" />
                                            </button>
                                            <button
                                                className="text-zinc-400 hover:text-zinc-950 disabled:opacity-30 dark:hover:text-white"
                                                onClick={() => onMoveColumn(col.id, 1)}
                                                disabled={colIdx === templateHeaders.length - 1}
                                                aria-label="Move column down"
                                                title="Move down"
                                            >
                                                <ArrowDownIcon className="size-3" />
                                            </button>
                                        </div>
                                        {isEditingName ? (
                                            <div className="flex w-32 shrink-0 items-center gap-1">
                                                <Input
                                                    autoFocus
                                                    value={columnNameDraft}
                                                    onChange={(e) => onColumnNameDraftChange(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && onCommitColumnName(col)}
                                                    onBlur={() => onCommitColumnName(col)}
                                                    className="!py-1 text-sm"
                                                />
                                            </div>
                                        ) : (
                                            <button
                                                className="w-32 shrink-0 truncate text-left text-sm font-medium hover:text-violet-600"
                                                title={col.displayName ? `${label} (originally "${col.originalName}")` : label}
                                                onClick={() => onStartEditColumnName(col)}
                                            >
                                                {label}
                                            </button>
                                        )}
                                        <Text className={`min-w-0 flex-1 truncate text-xs ${hasRule ? '!text-zinc-600 dark:!text-zinc-300' : '!text-zinc-400 italic'}`}>
                                            {statusText}
                                        </Text>
                                        {hasRule && (
                                            <button
                                                className="shrink-0 text-zinc-400 hover:text-red-600"
                                                onClick={() => onClearColumnRule(col.id)}
                                                aria-label="Clear"
                                                title="Clear"
                                            >
                                                <XMarkIcon className="size-4" />
                                            </button>
                                        )}
                                        <Button outline className="shrink-0 !px-2 !py-1 text-xs" onClick={() => onConfigureColumn(col.id)}>
                                            Configure
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex justify-end pt-3">
                            <Button onClick={onGenerate} disabled={generating || selectedColumns.size === 0}>
                                {generating ? 'Generating…' : `Generate report (${selectedColumns.size} columns)`}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    } else if (bubble.kind === 'generated') {
        content = (
            <div className="w-full max-w-xl rounded-xl border border-zinc-950/10 bg-white p-4 dark:border-white/10 dark:bg-zinc-900">
                <div className="flex items-center gap-3">
                    <DocumentIcon className="size-8 shrink-0 text-zinc-400" />
                    <div className="min-w-0 flex-1">
                        <Text>
                            <Strong>{summary?.rowCount ?? conversion?.generated_row_count}</Strong> rows generated
                        </Text>
                        <Input
                            value={outputFilenameDraft}
                            onChange={(e) => onFilenameDraftChange(e.target.value)}
                            onBlur={onSaveFilename}
                            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                            className="mt-1 !py-1 text-sm"
                            aria-label="Output file name"
                        />
                    </div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                    <Button outline onClick={() => onPreview('generated', conversion?.generated_file_name || 'Generated report')}>
                        <EyeIcon />
                        Preview
                    </Button>
                    <Button outline onClick={onOpenEditReport}>
                        <PencilIcon />
                        Edit report
                    </Button>
                    <Button onClick={onDownload}>
                        <DocumentArrowDownIcon />
                        Download
                    </Button>
                </div>
            </div>
        );
    }

    if (!content) return null;

    if (isUser) {
        return (
            <div className="bubble-enter flex justify-end py-1">
                <div className="flex max-w-[80%] flex-col items-end gap-1">
                    {content}
                    <span className="px-1 text-xs text-zinc-400">{formatTime(bubble.time)}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="bubble-enter flex items-start gap-3 py-1">
            <div className="w-7 shrink-0">{showAvatar && <AssistantAvatar />}</div>
            <div className="min-w-0 flex-1">
                {content}
                {bubble.kind === 'text' && <span className="mt-1 block px-0.5 text-xs text-zinc-400">{formatTime(bubble.time)}</span>}
            </div>
        </div>
    );
};

export default AIReportHelperPage;
